import { type ExportFormat, withExtension } from '../core/io/formats';
import { describePlan, planOpenedImage } from '../core/io/openPlan';
import { addLayer, createDocument, createImageLayer } from '../core/model/document';
import type { EditorDocument } from '../core/model/types';
import type { ExportOptions, ExportResult } from '../engine/types';
import {
  copyImageToClipboard,
  decodeImage,
  downloadBlob,
  downscaleDataUrl,
  fileToDataUrl,
} from '../io/imageIO';
import type { AppContext } from './context';
import { isProjectFile, openProjectFile } from './projectIO';

/**
 * Document-level import/export flows.
 *
 * These live in the app layer because they touch the browser, the engine and
 * the store — but every *decision* inside them is delegated to pure functions
 * in `core/io`, so the policy stays unit-tested without a browser.
 */

export function baseName(fileName: string): string {
  return fileName.replace(/\.[^.]+$/, '') || 'Untitled';
}

/** Build a document whose single layer is the given image. */
export function documentFromImage(
  src: string,
  width: number,
  height: number,
  name: string,
): EditorDocument {
  const doc = createDocument({ width, height, name, background: 'transparent' });
  return addLayer(doc, createImageLayer(src, width, height, { name }));
}

export interface OpenImageOutcome {
  ok: boolean;
  reason?: string;
  downscaled: boolean;
  document?: EditorDocument;
}

/**
 * Open an image file into the editor.
 *
 * Oversized images are never resized silently: the user is told the resulting
 * size and asked, because discarding pixels without asking is the kind of
 * surprise an editor should never spring.
 */
export async function openImageFile(
  file: File | Blob,
  ctx: AppContext,
  name = 'Pasted image',
): Promise<OpenImageOutcome> {
  const file_ = file instanceof File ? file : new File([file], name, { type: file.type });
  const { width, height } = await decodeImage(file_);
  const plan = planOpenedImage(width, height);

  if (plan.action === 'reject') {
    return { ok: false, reason: plan.reason, downscaled: false };
  }

  let src = await fileToDataUrl(file_);
  let finalWidth = width;
  let finalHeight = height;

  if (plan.action === 'downscale') {
    const accepted = await ctx.askConfirm({
      title: 'That image is larger than WebPaint can hold',
      message: `${describePlan(plan)} Resize it to fit?`,
      confirmLabel: 'Resize and open',
      cancelLabel: 'Keep it as it is',
    });
    if (!accepted) return { ok: false, reason: 'Cancelled', downscaled: false };
    finalWidth = plan.width;
    finalHeight = plan.height;
    src = await downscaleDataUrl(src, finalWidth, finalHeight, file_.type || 'image/png');
  }

  const doc = documentFromImage(src, finalWidth, finalHeight, baseName(file_.name || name));
  ctx.store.getState().loadDocument(doc);
  ctx.store.getState().zoomToFit();

  return { ok: true, downscaled: plan.action === 'downscale', document: doc };
}

/**
 * Open anything the editor understands, routing by file kind.
 *
 * One entry point matters: a project dropped on the canvas should open as a
 * project, not fail as a broken image, whichever gesture delivered it.
 */
export async function openAnyFile(file: File, ctx: AppContext): Promise<OpenImageOutcome> {
  if (isProjectFile(file)) {
    await openProjectFile(file, ctx);
    return { ok: true, downscaled: false, document: ctx.store.getState().doc };
  }
  return openImageFile(file, ctx);
}

export async function exportDocument(
  ctx: AppContext,
  options: ExportOptions,
): Promise<ExportResult> {
  const engine = ctx.getEngine();
  if (!engine) throw new Error('The canvas is not ready yet.');
  const doc = ctx.store.getState().doc;
  const result = await engine.exportBlob(options);
  return { ...result, fileName: withExtension(options.fileName ?? doc.name, options.format) };
}

export async function exportAndDownload(
  ctx: AppContext,
  format: ExportFormat,
  quality: number,
  scale: number,
): Promise<ExportResult> {
  const result = await exportDocument(ctx, { format, quality, scale });
  downloadBlob(result.blob, result.fileName);
  return result;
}

export async function copyCurrentImage(ctx: AppContext): Promise<void> {
  const result = await exportDocument(ctx, { format: 'png' });
  await copyImageToClipboard(result.blob);
}
